import { z } from "zod"

// Display profile for a mirrored Clerk user. Attached to responses that
// reference a `*UserId` so no raw Clerk id ever has to render in the UI.
// All profile fields are nullable: rows can exist before the webhook or
// backfill has filled them in, and deleted users keep only their name.
export const ApiUserSummarySchema = z.object({
  userId: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  imageUrl: z.string().nullable(),
})

export type ApiUserSummary = z.infer<typeof ApiUserSummarySchema>

/**
 * The in-app review ask.
 *
 * `eligible` is computed entirely on the server, from state the server owns:
 * how many exports the workspace has produced, when this person was last
 * asked, and whether they have already answered permanently. The client's only
 * job is to render the prompt and report what happened, which is what keeps
 * "do not ask me again" working on a second laptop.
 *
 * `reason` is carried on the negative case so a support question ("why am I
 * not seeing it") has an answer that does not require reading the database.
 */
export const apiReviewPromptReasons = [
  "not_enough_exports",
  "asked_recently",
  "already_clicked",
  "dismissed",
  "degraded",
] as const

export const ApiReviewPromptStatusSchema = z.object({
  eligible: z.boolean(),
  reason: z.enum(apiReviewPromptReasons).nullable(),
  /** Lifetime exports this workspace has produced. */
  exportCount: z.number().int().nonnegative(),
  /** Where the review is written. One constant, served rather than hardcoded
   * in the client, so changing it does not need a web deploy. */
  reviewUrl: z.string().url(),
})

export type ApiReviewPromptStatus = z.infer<typeof ApiReviewPromptStatusSchema>

/**
 * What the user did with the prompt.
 *
 * `shown` is reported by the client rather than written when the status is
 * read, because reading eligibility is not the same as showing the prompt: the
 * client may check and then decide not to render (a degraded state, a route
 * change). Writing `lastAskedAt` on the read would start a 90 day cooldown for
 * an ask that never happened.
 *
 * There is deliberately no `not_now` action. Closing the prompt without
 * answering is already recorded by `shown`, which starts the 90 day gap, so a
 * second event would say nothing the first one did not. `dismissed` means the
 * permanent answer, and nothing in the product reverses it.
 */
export const ApiReviewPromptEventBodySchema = z.object({
  action: z.enum(["shown", "clicked", "dismissed"]),
})

export type ApiReviewPromptEventBody = z.infer<
  typeof ApiReviewPromptEventBodySchema
>
